/*
 * Bundle builder page: filter pills, ring size picker, quick add and the bundle panel.
 * Markup lives in sections/bundle-builder.liquid and snippets/bundle-builder-card.liquid.
 *
 * Deep link a pill with ?category=<pill label handle>, e.g. /pages/bundle-builder?category=rings
 *
 * The bundle is kept in this.items (and localStorage) and announced with a
 * `bundle-builder:change` event ({ items }).
 *
 * Tiers ("6:20, 7:22, ...") come from the Nominal Bundles app's shop metafield, or the
 * section's fallback setting (see data-tiers in the section). The target
 * tier is the pack the shopper is filling (first tier by default, or one they tap).
 * The discount is the best tier the bundle has reached; the largest pack is the cap.
 */
const SIZE_OPTIONS = ['ring size', 'size'];
const STORAGE_KEY = 'nominal-bundle-builder';

class BundleBuilder extends HTMLElement {
  connectedCallback() {
    this.pills = Array.from(this.querySelectorAll('[data-bb-filter]'));
    this.grid = this.querySelector('[data-bb-grid]');
    this.tiers = this.parseTiers(this.dataset.tiers);
    this.targetIndex = 0;
    this.items = this.loadItems().slice(0, this.maxItems());
    this.picking = null;
    this.cards = [];
    this.cardByEl = new WeakMap();
    this.orders = new Map();
    this.addCards(this.querySelectorAll('[data-bb-card]'));
    this.sizesWrap = this.querySelector('[data-bb-sizes]');
    this.sizeList = this.querySelector('[data-bb-size-list]');
    this.empty = this.querySelector('[data-bb-empty]');

    // Desktop sizes the product scroll box to the space left under the sticky pills.
    this.controls = this.querySelector('[data-bb-controls]');
    new ResizeObserver(() => {
      this.style.setProperty('--bb-controls-height', `${this.controls.offsetHeight}px`);
    }).observe(this.controls);

    this.activePill = null;
    this.selectedSize = null;

    this.pills.forEach((pill) => pill.addEventListener('click', () => this.selectPill(pill)));
    this.sizeList.addEventListener('click', (event) => {
      const button = event.target.closest('[data-size]');
      if (button) this.selectSize(button.dataset.size);
    });

    this.grid?.addEventListener('click', (event) => this.onGridClick(event));
    // composedPath, not contains(): picking a value re-renders the picker, detaching the clicked button.
    document.addEventListener('click', (event) => {
      if (this.picking && !event.composedPath().includes(this.picking.card.el)) this.closePicker();
    });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.picking) this.closePicker({ restoreFocus: true });
    });

    this.panel = this.querySelector('[data-bb-panel]');
    this.panel.querySelector('[data-bb-toggle]').addEventListener('click', () => this.togglePanel());
    this.panel.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-bb-remove]');
      if (remove) this.removeItem(Number(remove.dataset.bbRemove));
      if (event.target.closest('[data-bb-cart]')) this.addBundleToCart();
    });
    this.querySelectorAll('[data-bb-tiers]').forEach((el) =>
      el.addEventListener('click', (event) => {
        const tier = event.target.closest('[data-tier-index]');
        if (tier) this.selectTier(Number(tier.dataset.tierIndex));
      })
    );
    this.renderOffer();
    this.renderBundle();

    const category = new URLSearchParams(window.location.search).get('category');
    const start = this.pills.find((pill) => pill.dataset.handle === category) || this.pills[0];
    if (start) this.selectPill(start, { scroll: false });
    this.pills.forEach((pill) => this.loadOrder(pill));

    this.loadRemainingPages();
  }

  addCards(elements) {
    elements.forEach((el) => {
      const card = {
        el,
        productId: el.dataset.productId,
        index: this.cards.length,
        tags: this.parseList(el.dataset.tags).map((tag) => tag.toLowerCase()),
        sizes: this.parseList(el.dataset.sizes),
      };
      this.cards.push(card);
      this.cardByEl.set(el, card);
      this.renderCardState(card);
    });
  }

  // Fetch the rest of the collection with the Section Rendering API and append it to the grid.
  async loadRemainingPages() {
    let nextUrl = this.grid?.dataset.nextUrl;
    while (nextUrl) {
      const url = new URL(nextUrl, window.location.origin);
      url.searchParams.set('section_id', this.dataset.sectionId);
      let html;
      try {
        html = await (await fetch(url)).text();
      } catch (error) {
        return;
      }

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const cards = Array.from(doc.querySelectorAll('[data-bb-card]'));
      this.grid.append(...cards);
      this.addCards(cards);
      this.renderSizes();
      this.applyFilters();
      this.applyOrder();

      nextUrl = doc.querySelector('[data-bb-grid]')?.dataset.nextUrl;
    }
  }

  parseList(value) {
    try {
      return JSON.parse(value || '[]');
    } catch (error) {
      return [];
    }
  }

  selectPill(pill, { scroll = true } = {}) {
    this.closePicker();
    this.activePill = pill;
    this.pills.forEach((p) => p.setAttribute('aria-pressed', String(p === pill)));
    if (scroll) pill.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });

    this.showSizePicker = pill.dataset.sizePicker === 'true';
    if (!this.showSizePicker) this.selectedSize = null;
    this.renderSizes();
    this.applyFilters();
    this.applyOrder();
    if (scroll) this.scrollToTop();
  }

  selectSize(size) {
    this.closePicker();
    this.selectedSize = this.selectedSize === size ? null : size;
    this.renderSizes();
    this.applyFilters();
    this.scrollToTop();
  }

  // After a filter change, show the first results: reset the desktop scroll box,
  // and on mobile scroll the page back up if the grid top is hidden under the sticky pills.
  scrollToTop() {
    if (!this.grid) return;
    this.grid.scrollTop = 0;
    const hidden = this.controls.getBoundingClientRect().bottom - this.grid.getBoundingClientRect().top;
    if (hidden > 0) window.scrollBy({ top: -hidden });
  }

  matchesPill(card) {
    const tag = (this.activePill?.dataset.tag || '').toLowerCase();
    return !tag || card.tags.includes(tag);
  }

  renderSizes() {
    this.sizesWrap.hidden = !this.showSizePicker;
    if (!this.showSizePicker) return;

    const sizes = new Set();
    this.cards.filter((card) => this.matchesPill(card)).forEach((card) => card.sizes.forEach((s) => sizes.add(s)));
    const sorted = Array.from(sizes).sort((a, b) => parseFloat(a) - parseFloat(b) || a.localeCompare(b));

    this.sizeList.replaceChildren(
      ...sorted.map((size) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'bb-size';
        button.dataset.size = size;
        button.textContent = size;
        button.setAttribute('aria-pressed', String(size === this.selectedSize));
        return button;
      })
    );
  }

  applyFilters() {
    let visible = 0;
    this.cards.forEach((card) => {
      const show = this.matchesPill(card) && (!this.selectedSize || card.sizes.includes(this.selectedSize));
      card.el.hidden = !show;
      if (show) visible += 1;
    });
    if (this.empty) this.empty.hidden = visible > 0;

    this.dispatchEvent(
      new CustomEvent('bundle-builder:filter', {
        bubbles: true,
        detail: { tag: this.activePill?.dataset.tag || '', size: this.selectedSize },
      })
    );
  }

  /* ---------- Pill sort order ---------- */

  // A pill with a sort collection (data-sort-url) shows its cards in that collection's order.
  // Resolves to Map(productId -> position), fetched once per collection.
  loadOrder(pill) {
    const sortUrl = pill?.dataset.sortUrl;
    if (!sortUrl) return null;
    if (!this.orders.has(sortUrl)) this.orders.set(sortUrl, this.fetchOrder(sortUrl));
    return this.orders.get(sortUrl);
  }

  // Reads the product IDs from sections/bundle-builder-order, page by page.
  async fetchOrder(sortUrl) {
    const ranks = new Map();
    let nextUrl = sortUrl;
    while (nextUrl) {
      const url = new URL(nextUrl, window.location.origin);
      url.searchParams.set('section_id', 'bundle-builder-order');
      let html;
      try {
        const response = await fetch(url);
        if (!response.ok) break;
        html = await response.text();
      } catch (error) {
        break;
      }

      const doc = new DOMParser().parseFromString(html, 'text/html');
      doc.querySelectorAll('[data-bb-order] [data-product-id]').forEach((el) => {
        if (!ranks.has(el.dataset.productId)) ranks.set(el.dataset.productId, ranks.size);
      });
      nextUrl = doc.querySelector('[data-bb-order]')?.dataset.nextUrl;
    }
    return ranks;
  }

  // Sort the grid by the active pill's collection. Cards not in it (or every card, when the
  // pill has no sort collection) keep the main collection's order, after the sorted ones.
  async applyOrder() {
    if (!this.grid) return;
    const pill = this.activePill;
    const ranks = (await this.loadOrder(pill)) || new Map();
    if (pill !== this.activePill) return;

    const rank = (card) => ranks.get(card.productId) ?? Infinity;
    const sorted = [...this.cards].sort((a, b) => rank(a) - rank(b) || a.index - b.index);
    if (sorted.every((card, i) => this.grid.children[i] === card.el)) return;
    this.grid.append(...sorted.map((card) => card.el));
  }

  /* ---------- Quick add ---------- */

  onGridClick(event) {
    const card = this.cardByEl.get(event.target.closest('[data-bb-card]'));
    if (!card) return;

    if (event.target.closest('[data-bb-add]')) {
      if (!this.isFull()) this.openPicker(card);
    } else if (event.target.closest('[data-bb-remove-one]')) {
      this.removeLastOf(card);
    } else if (event.target.closest('[data-bb-close]')) {
      this.closePicker({ restoreFocus: true });
    } else {
      const value = event.target.closest('[data-bb-value]');
      if (value && !value.disabled) this.chooseValue(value.dataset.bbValue);
    }
  }

  productFor(card) {
    if (!card.product) card.product = JSON.parse(card.el.querySelector('[data-bb-product]').textContent);
    return card.product;
  }

  // Start picking options for a product. A ring size chosen in the size picker is used when it's in stock.
  openPicker(card) {
    if (this.picking && this.picking.card !== card) this.closePicker();
    const product = this.productFor(card);
    const selections = product.options.map(() => null);

    if (this.selectedSize) {
      product.options.forEach((name, index) => {
        if (!SIZE_OPTIONS.includes(name.toLowerCase())) return;
        if (this.valuesFor(product, selections, index).available.has(this.selectedSize)) {
          selections[index] = this.selectedSize;
        }
      });
    }

    this.picking = { card, product, selections };
    this.advance();
  }

  chooseValue(value) {
    const { selections } = this.picking;
    selections[this.picking.index] = value;
    this.advance();
  }

  // Fill options that only have one value, then ask for the next open one. Once all are set, add the variant.
  advance() {
    const { card, product, selections } = this.picking;
    product.options.forEach((_, index) => {
      if (selections[index] !== null) return;
      const { all } = this.valuesFor(product, selections, index);
      if (all.length === 1) selections[index] = all[0];
    });

    const next = this.optionOrder(product).find((i) => selections[i] === null) ?? -1;
    if (next === -1) {
      const variant = product.variants.find((v) => v.available && v.options.every((o, i) => o === selections[i]));
      this.closePicker({ restoreFocus: true });
      if (variant) this.addItem(card, product, variant);
      return;
    }

    this.picking.index = next;
    this.renderPicker(next);
  }

  // Values for one option, given the other options already picked. `available` = in stock.
  valuesFor(product, selections, index) {
    const all = [];
    const available = new Set();
    product.variants.forEach((variant) => {
      const matches = variant.options.every((value, i) => i === index || selections[i] === null || selections[i] === value);
      if (!matches) return;
      const value = variant.options[index];
      if (!all.includes(value)) all.push(value);
      if (variant.available) available.add(value);
    });
    return { all, available };
  }

  renderPicker(index) {
    const { card, product, selections } = this.picking;
    const picker = card.el.querySelector('[data-bb-picker]');
    const name = product.options[index];
    const { all, available } = this.valuesFor(product, selections, index);

    const head = document.createElement('div');
    head.className = 'bb-picker__head';
    const title = document.createElement('p');
    title.className = 'bb-picker__title';
    title.textContent = `Choose ${this.optionLabel(name)}`;
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'bb-picker__close';
    close.dataset.bbClose = '';
    close.setAttribute('aria-label', 'Close');
    close.textContent = '\u00d7';
    head.append(title, close);

    const picked = this.optionOrder(product)
      .map((i) => (selections[i] === null ? null : this.valueLabel(product.options[i], selections[i])))
      .filter(Boolean)
      .join(' · ');
    const summary = document.createElement('p');
    summary.className = 'bb-picker__summary';
    summary.textContent = picked;
    summary.hidden = !picked;

    const values = document.createElement('div');
    values.className = 'bb-picker__values';
    values.setAttribute('role', 'group');
    values.setAttribute('aria-label', title.textContent);
    all.forEach((value) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'bb-picker__value';
      button.dataset.bbValue = value;
      button.disabled = !available.has(value);
      if (this.isMetal(name)) {
        const swatch = document.createElement('span');
        swatch.className = 'bb-picker__swatch';
        swatch.dataset.metal = this.valueLabel(name, value).toLowerCase().split(' ')[0];
        button.append(swatch);
      }
      button.append(this.valueLabel(name, value));
      values.append(button);
    });

    picker.replaceChildren(head, summary, values);
    picker.hidden = false;
    card.el.classList.add('is-picking');
    values.querySelector('button:not(:disabled)')?.focus({ preventScroll: true });
  }

  closePicker({ restoreFocus = false } = {}) {
    if (!this.picking) return;
    const { card } = this.picking;
    card.el.querySelector('[data-bb-picker]').hidden = true;
    card.el.classList.remove('is-picking');
    this.picking = null;
    if (restoreFocus) card.el.querySelector('[data-bb-add]:not([hidden])')?.focus({ preventScroll: true });
  }

  // Option indexes with metal first, then the rest in the product's order.
  optionOrder(product) {
    const indexes = product.options.map((_, i) => i);
    return indexes.sort((a, b) => this.isMetal(product.options[b]) - this.isMetal(product.options[a]));
  }

  isMetal(optionName) {
    return ['color', 'metal'].includes(optionName.toLowerCase());
  }

  optionLabel(optionName) {
    return this.isMetal(optionName) ? 'metal' : optionName.toLowerCase();
  }

  // "18K Gold Plated" reads as "Gold" in the picker and bundle.
  valueLabel(optionName, value) {
    if (!this.isMetal(optionName)) return value;
    return value.replace(/^18K\s+/i, '').replace(/\s+Plated$/i, '');
  }

  /* ---------- Bundle ---------- */

  addItem(card, product, variant) {
    const image = variant.image || card.el.querySelector('.bb-card__img')?.currentSrc || null;
    this.items.push({
      productId: String(product.id),
      variantId: variant.id,
      title: product.title,
      variantTitle: this.optionOrder(product)
        .map((i) => this.valueLabel(product.options[i], variant.options[i]))
        .filter((label) => label !== 'Default Title')
        .join(' · '),
      image,
      price: variant.price,
    });
    this.onItemsChanged(card);
  }

  removeLastOf(card) {
    const index = this.items.map((item) => item.productId).lastIndexOf(card.productId);
    if (index !== -1) this.removeItem(index);
  }

  removeItem(index) {
    const [item] = this.items.splice(index, 1);
    if (!item) return;
    const card = this.cards.find((c) => c.productId === item.productId);
    this.onItemsChanged(card);
  }

  onItemsChanged(card) {
    this.saveItems();
    if (card) this.renderCardState(card);
    this.renderBundle();
    this.dispatchEvent(new CustomEvent('bundle-builder:change', { bubbles: true, detail: { items: [...this.items] } }));
  }

  renderCardState(card) {
    const count = this.items.filter((item) => item.productId === card.productId).length;
    const badge = card.el.querySelector('[data-bb-badge]');
    badge.hidden = count === 0;
    badge.textContent = `${count} in bundle`;
    card.el.querySelector('.bb-card__add').hidden = count > 0;
    card.el.querySelector('[data-bb-stepper]').hidden = count === 0;
    card.el.querySelector('[data-bb-count]').textContent = `${count} added`;
    card.el.classList.toggle('is-in-bundle', count > 0);

    const full = this.isFull();
    card.el.querySelectorAll('[data-bb-add]').forEach((button) => {
      button.disabled = full;
    });

    const priceEl = card.el.querySelector('[data-bb-price-el]');
    const price = Number(priceEl.dataset.price);
    const pct = this.discountPercent();
    const sale = document.createElement('span');
    sale.textContent = this.formatMoney(Math.round(price * (1 - pct / 100)));
    if (pct > 0) {
      const was = document.createElement('s');
      was.className = 'bb-card__compare';
      was.textContent = this.formatMoney(price);
      priceEl.replaceChildren(was, sale);
    } else {
      priceEl.replaceChildren(sale);
    }
  }

  /* ---------- Tiers and bundle panel ---------- */

  // "6:20, 7:22" -> [{ size: 6, percent: 20 }, { size: 7, percent: 22 }], sorted by size.
  parseTiers(value) {
    const tiers = (value || '')
      .split(',')
      .map((part) => part.split(':').map((n) => parseFloat(n)))
      .filter(([size, percent]) => size > 0 && percent >= 0)
      .map(([size, percent]) => ({ size: Math.round(size), percent }))
      .sort((a, b) => a.size - b.size);
    return tiers.length ? tiers : [{ size: 6, percent: 20 }];
  }

  maxItems() {
    return this.tiers[this.tiers.length - 1].size;
  }

  isFull() {
    return this.items.length >= this.maxItems();
  }

  // Best tier the bundle has reached, or null.
  reachedTier() {
    return [...this.tiers].reverse().find((tier) => this.items.length >= tier.size) || null;
  }

  // Price shown everywhere: the reached tier's discount, or the target tier's before any is reached.
  discountPercent() {
    return (this.reachedTier() || this.tiers[this.targetIndex]).percent;
  }

  selectTier(index) {
    if (!this.tiers[index] || this.tiers[index].size < this.items.length) return;
    this.targetIndex = index;
    this.renderBundle();
  }

  togglePanel(open = !this.panel.classList.contains('is-open')) {
    this.panel.classList.toggle('is-open', open);
    this.panel.querySelector('[data-bb-toggle]').setAttribute('aria-expanded', String(open));
  }

  renderBundle() {
    const count = this.items.length;
    // Move the target up when the bundle outgrows it.
    while (this.targetIndex < this.tiers.length - 1 && count > this.tiers[this.targetIndex].size) {
      this.targetIndex += 1;
    }
    const target = this.tiers[this.targetIndex];
    const reached = this.reachedTier();
    const next = this.tiers.find((tier) => tier.size > count);
    const pct = this.discountPercent();

    // Re-price tiles and lock Add buttons when the discount or the cap changes.
    const cardKey = `${pct}|${this.isFull()}`;
    if (cardKey !== this.cardKey) {
      this.cardKey = cardKey;
      this.cards.forEach((card) => this.renderCardState(card));
    }

    this.querySelector('[data-bb-title]').textContent = `Your Bundle (${count}/${target.size})`;
    let message;
    if (!reached) message = `Add ${target.size - count} more to get ${target.percent}% off`;
    else if (next) message = `${reached.percent}% off · ${next.size - count} more for ${next.percent}%`;
    else message = `Max savings: ${reached.percent}% off`;
    this.querySelector('[data-bb-message]').textContent = message;
    this.querySelector('[data-bb-progress]').style.width = `${Math.min(count / target.size, 1) * 100}%`;

    this.querySelectorAll('[data-bb-tiers]').forEach((el) => this.renderTiers(el, reached));
    this.renderItems(target.size);

    const subtotal = this.items.reduce((sum, item) => sum + item.price, 0);
    const bundlePrice = Math.round(subtotal * (1 - pct / 100));
    // Footer row, like PSD: "Total: <full> <bundle price>" + "You are saving N% off", button on the right.
    const footFull = this.querySelector('[data-bb-foot-full]');
    footFull.textContent = subtotal > bundlePrice ? this.formatMoney(subtotal) : '';
    footFull.hidden = !footFull.textContent;
    this.querySelector('[data-bb-foot-price]').textContent = this.formatMoney(bundlePrice);
    const saving = this.querySelector('[data-bb-saving]');
    if (count > 0 && pct > 0) {
      saving.textContent = this.isFull() ? `Max savings reached: ${pct}% off` : `You are saving ${pct}% off`;
    } else {
      saving.textContent = '';
    }
    saving.hidden = !saving.textContent;

    const cta = this.querySelector('[data-bb-cart]');
    if (!this.adding) {
      cta.disabled = !reached;
      cta.textContent = reached ? 'Add to cart' : `Add ${this.tiers[0].size - count} more`;
    }
    this.panel.classList.toggle('is-ready', !!reached);
    this.panel.classList.toggle('is-empty', count === 0);
  }

  // One line under the heading, e.g. "6 pieces = 20% off · up to 35% off at 10".
  renderOffer() {
    const offer = this.querySelector('[data-bb-offer]');
    if (!offer) return;
    const first = this.tiers[0];
    const last = this.tiers[this.tiers.length - 1];
    const bold = (text) => {
      const b = document.createElement('b');
      b.textContent = text;
      return b;
    };
    const parts = [bold(`${first.size} pieces = ${first.percent}% off`)];
    if (last !== first) parts.push(' · up to ', bold(`${last.percent}% off`), ` at ${last.size}`);
    offer.replaceChildren(...parts);
  }

  renderTiers(el, reached) {
    el.replaceChildren(
      ...this.tiers.map((tier, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'bb-tier';
        button.dataset.tierIndex = index;
        button.setAttribute('aria-pressed', String(index === this.targetIndex));
        button.disabled = tier.size < this.items.length;
        button.classList.toggle('is-reached', !!reached && tier.size <= reached.size);
        const size = document.createElement('span');
        size.className = 'bb-tier__size';
        size.textContent = `${tier.size}-pack`;
        const pct = document.createElement('span');
        pct.className = 'bb-tier__percent';
        pct.textContent = `${tier.percent}%`;
        const off = document.createElement('span');
        off.className = 'bb-tier__off';
        off.textContent = 'off';
        button.append(size, pct, off);
        return button;
      })
    );
  }

  renderItems(slots) {
    const list = this.querySelector('[data-bb-items]');
    const rows = this.items.map((item, index) => {
      const li = document.createElement('li');
      li.className = 'bb-item';
      const media = document.createElement('div');
      media.className = 'bb-item__media';
      if (item.image) {
        const img = document.createElement('img');
        img.src = item.image;
        img.alt = '';
        img.width = 96;
        img.height = 96;
        img.loading = 'lazy';
        media.append(img);
      }
      const info = document.createElement('div');
      info.className = 'bb-item__info';
      const title = document.createElement('span');
      title.className = 'bb-item__title';
      title.textContent = item.title;
      const variant = document.createElement('span');
      variant.className = 'bb-item__variant';
      variant.textContent = [item.variantTitle, this.formatMoney(item.price)].filter(Boolean).join(' · ');
      info.append(title, variant);
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'bb-item__remove';
      remove.dataset.bbRemove = index;
      remove.setAttribute('aria-label', `Remove ${item.title}`);
      remove.textContent = '\u00d7';
      li.append(media, info, remove);
      return li;
    });
    for (let i = this.items.length; i < slots; i += 1) {
      const empty = document.createElement('li');
      empty.className = 'bb-item bb-item--empty';
      empty.setAttribute('aria-hidden', 'true');
      rows.push(empty);
    }
    list.replaceChildren(...rows);
  }

  async addBundleToCart() {
    const count = this.items.length;
    if (!this.reachedTier() || this.adding) return;
    const cta = this.querySelector('[data-bb-cart]');
    const error = this.querySelector('[data-bb-error]');
    this.adding = true;
    cta.disabled = true;
    cta.textContent = 'Adding…';
    error.hidden = true;

    // Every piece carries the same bundle id; the Cart Transform (NOM-8) merges them into one line.
    const bundleId = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const properties = { _bundle_id: bundleId, _bundle_size: String(count) };
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: this.items.map((item) => ({ id: item.variantId, quantity: 1, properties })) }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || 'Could not add your bundle.');
    } catch (err) {
      this.adding = false;
      error.textContent = err.message;
      error.hidden = false;
      this.renderBundle();
      return;
    }

    this.adding = false;
    const addedItems = this.items;
    this.items = [];
    this.targetIndex = 0;
    this.togglePanel(false);
    this.onItemsChanged();
    this.cards.forEach((card) => this.renderCardState(card));
    this.trackAddToCart(addedItems, bundleId);
    this.openCart(count);
  }

  // For Google Tag Manager. Shopify's own add-to-cart tracking (pixels) also sees the cart add.
  trackAddToCart(items, bundleId) {
    const tier = [...this.tiers].reverse().find((t) => items.length >= t.size);
    const percent = tier ? tier.percent : 0;
    const subtotal = items.reduce((sum, item) => sum + item.price, 0);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'bundle_add_to_cart',
      bundle_id: bundleId,
      bundle_size: items.length,
      bundle_discount_percent: percent,
      currency: window.Shopify?.currency?.active,
      value: Math.round(subtotal * (1 - percent / 100)) / 100,
      items: items.map((item) => ({
        item_id: String(item.variantId),
        item_name: item.title,
        item_variant: item.variantTitle,
        price: item.price / 100,
        quantity: 1,
      })),
    });
  }

  // The theme's cart drawer re-renders and opens on `product:added`, the same event its
  // own add-to-cart forms fire. Without the drawer, go to the cart page.
  openCart(quantity) {
    if (document.getElementById('sidebar-cart')) {
      document.dispatchEvent(new CustomEvent('product:added', { bubbles: true, detail: { quantity } }));
    } else {
      window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
    }
  }

  formatMoney(cents) {
    const format = (this.dataset.moneyFormat || '${{amount}}').replace(/<[^>]*>/g, '');
    const group = (whole, sep) => whole.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    const [whole, decimals] = (cents / 100).toFixed(2).split('.');
    // "No decimals" formats round to the nearest dollar, like Shopify's money filter.
    const rounded = String(Math.round(cents / 100));
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      if (key === 'amount_no_decimals') return group(rounded, ',');
      if (key === 'amount_with_comma_separator') return `${group(whole, '.')},${decimals}`;
      if (key === 'amount_no_decimals_with_comma_separator') return group(rounded, '.');
      return `${group(whole, ',')}.${decimals}`;
    });
  }

  loadItems() {
    try {
      const items = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(items) ? items.filter((item) => item && item.variantId && item.productId) : [];
    } catch (error) {
      return [];
    }
  }

  saveItems() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.items));
    } catch (error) {
      // Storage can be blocked (private mode); the bundle still works for this visit.
    }
  }
}

if (!customElements.get('bundle-builder')) customElements.define('bundle-builder', BundleBuilder);
